import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/i18n";
import { Wallet, TrendingUp } from "lucide-react";

/**
 * A20-P4 — User dashboard mini-tiles aggregating the caller's budget data.
 *
 * Strict per-user data (the backend filters by owner_id). EUR-only.
 * Renders nothing visually intrusive when the user has no budgets — the
 * tile still shows an empty state so the user discovers the feature.
 *
 * No props — fetches `/api/calendar/budget/analytics` on mount.
 */
const fmtEur = (n) => {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency", currency: "EUR", maximumFractionDigits: 0,
    }).format(Number(n) || 0);
  } catch { return `€${Math.round(Number(n) || 0)}`; }
};

const ZERO = {
  currency: "EUR",
  totals: {
    estimated_total_sum: 0, actual_total_sum: 0,
    variance_total: 0, variance_percentage: 0,
    budgets_count: 0, expense_lines_count: 0,
  },
  top_categories: [],
  status_breakdown: { budgets: {}, lines: {} },
};

export const BudgetAnalyticsTiles = () => {
  const { t } = useLang();
  const [data, setData] = useState(ZERO);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get("/calendar/budget/analytics")
      .then((r) => { if (alive) { setData(r.data || ZERO); setLoaded(true); } })
      .catch(() => { if (alive) { setData(ZERO); setLoaded(true); } });
    return () => { alive = false; };
  }, []);

  if (!loaded) return null;
  const { totals, top_categories } = data;
  const hasData = totals.budgets_count > 0 || totals.expense_lines_count > 0;

  const variance = Number(totals.variance_total || 0);
  const over = variance > 0;
  const under = variance < 0;
  const pct = Number(totals.variance_percentage || 0);

  return (
    <div className="tsw-card p-5 space-y-4" data-testid="dashboard-budget-tile">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="font-display font-semibold text-base text-slate-900 inline-flex items-center gap-2">
          <Wallet className="w-4 h-4 text-emerald-700" />
          {t("budget.analytics.sectionTitle")}
        </h3>
        {hasData && (
          <span className="text-[11px] text-slate-500">
            {totals.budgets_count} {t("budget.analytics.budgetsCount")} · {totals.expense_lines_count} {t("budget.analytics.linesCount")}
          </span>
        )}
      </div>

      {!hasData ? (
        <p className="text-sm text-slate-500" data-testid="dashboard-budget-empty">
          {t("budget.analytics.empty")}
        </p>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs" data-testid="dashboard-budget-totals">
            <div className="bg-slate-50 border rounded p-2">
              <div className="text-slate-500 text-[10px] uppercase tracking-wide">{t("budget.totals.estimated")}</div>
              <div className="font-semibold tabular-nums text-slate-900" data-testid="dashboard-budget-estimated">{fmtEur(totals.estimated_total_sum)}</div>
            </div>
            <div className="bg-slate-50 border rounded p-2">
              <div className="text-slate-500 text-[10px] uppercase tracking-wide">{t("budget.totals.actual")}</div>
              <div className="font-semibold tabular-nums text-slate-900" data-testid="dashboard-budget-actual">{fmtEur(totals.actual_total_sum)}</div>
            </div>
            <div className={`border rounded p-2 ${over ? "bg-rose-50 border-rose-200" : under ? "bg-emerald-50 border-emerald-200" : "bg-slate-50"}`}>
              <div className="text-slate-500 text-[10px] uppercase tracking-wide">{t("budget.totals.variance")}</div>
              <div className={`font-semibold tabular-nums ${over ? "text-rose-700" : under ? "text-emerald-700" : "text-slate-900"}`} data-testid="dashboard-budget-variance">
                {variance > 0 ? "+" : ""}{fmtEur(variance)}
                {totals.estimated_total_sum > 0 && (
                  <span className="text-[10px] text-slate-500 ms-1">
                    ({pct > 0 ? "+" : ""}{pct.toFixed(1)}%)
                  </span>
                )}
              </div>
            </div>
            <div className="bg-slate-50 border rounded p-2">
              <div className="text-slate-500 text-[10px] uppercase tracking-wide">{t("budget.totals.lineCount")}</div>
              <div className="font-semibold tabular-nums text-slate-900" data-testid="dashboard-budget-itemcount">{totals.expense_lines_count}</div>
            </div>
          </div>

          <div className="pt-2 border-t border-slate-100">
            <div className="text-xs font-medium text-slate-700 mb-1.5 inline-flex items-center gap-1">
              <TrendingUp className="w-3.5 h-3.5" />
              {t("budget.analytics.topCategories")}
            </div>
            {top_categories.length === 0 ? (
              <p className="text-xs text-slate-500" data-testid="dashboard-budget-top-empty">
                {t("budget.analytics.noCategoryYet")}
              </p>
            ) : (
              <ul className="space-y-1" data-testid="dashboard-budget-top-list">
                {top_categories.map((c, i) => {
                  const amount = c.actual_amount > 0 ? c.actual_amount : c.estimated_amount;
                  return (
                    <li
                      key={c.category}
                      className="flex items-center justify-between bg-slate-50 border rounded px-2 py-1.5 text-xs"
                      data-testid={`dashboard-budget-top-${c.category}`}
                    >
                      <span className="inline-flex items-center gap-2 truncate">
                        <span className="font-mono text-slate-400 tabular-nums">#{i + 1}</span>
                        <span className="font-medium text-slate-800 truncate">
                          {t(`budget.categories.${c.category}`) || c.category}
                        </span>
                        <span className="text-slate-400 hidden sm:inline">· {c.lines_count}</span>
                      </span>
                      <span className="font-semibold tabular-nums text-slate-900 shrink-0">
                        {fmtEur(amount)}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default BudgetAnalyticsTiles;
